import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Cable, Plus, RefreshCw, Trash2, Copy, Check, Power, PowerOff,
  History, HelpCircle, ChevronDown, ChevronUp, Terminal, ArrowRight,
  Database, Activity, KeyRound, ExternalLink, AlertTriangle, CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_ICONS = {
  sap_erp: '🔷', oracle_procurement: '🔶', netsuite: '🟣',
  quickbooks: '🟢', xero: '🔵', p_card: '💳',
  travel_expense: '✈', microsoft_dynamics: '🟦', sage: '🟩',
  coupa: '🔴', ariba: '🟠', custom_api: '⚙',
};

export default function Connectors() {
  const [connectors, setConnectors] = useState([]);
  const [sourceTypes, setSourceTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showKeyReveal, setShowKeyReveal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [revealedKey, setRevealedKey] = useState('');
  const [revealConnectorName, setRevealConnectorName] = useState('');
  const [revealConnectorId, setRevealConnectorId] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyConnectorName, setHistoryConnectorName] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formSource, setFormSource] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formMapping, setFormMapping] = useState({});

  const fetchConnectors = useCallback(async () => {
    try {
      const res = await api.get('/connectors');
      setConnectors(res.data.connectors || []);
    } catch {
      toast.error('Failed to load connectors');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSourceTypes = useCallback(async () => {
    try {
      const res = await api.get('/connectors/source-types');
      setSourceTypes(res.data.source_types || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchConnectors(); fetchSourceTypes(); }, [fetchConnectors, fetchSourceTypes]);

  const handleSourceChange = (val) => {
    setFormSource(val);
    const st = sourceTypes[val];
    if (st) setFormMapping(st.default_mapping || {});
  };

  const handleCreate = async () => {
    if (!formName.trim() || !formSource) {
      toast.error('Name and source type are required');
      return;
    }
    setCreateLoading(true);
    try {
      const res = await api.post('/connectors', {
        name: formName.trim(),
        source_type: formSource,
        description: formDesc.trim(),
        field_mapping: formMapping,
      });
      setRevealedKey(res.data.api_key);
      setRevealConnectorName(formName.trim());
      setRevealConnectorId(res.data.connector?.id || '');
      setShowCreate(false);
      setShowKeyReveal(true);
      setFormName(''); setFormSource(''); setFormDesc(''); setFormMapping({});
      fetchConnectors();
      toast.success('Connector created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create connector');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggle = async (c) => {
    try {
      await api.put(`/connectors/${c.id}`, { is_active: !c.is_active });
      fetchConnectors();
      toast.success(`Connector ${c.is_active ? 'deactivated' : 'activated'}`);
    } catch {
      toast.error('Failed to update connector');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/connectors/${id}`);
      setShowDeleteConfirm(null);
      fetchConnectors();
      toast.success('Connector deleted');
    } catch {
      toast.error('Failed to delete connector');
    }
  };

  const handleRegenKey = async (c) => {
    try {
      const res = await api.post(`/connectors/${c.id}/regenerate-key`);
      setRevealedKey(res.data.api_key);
      setRevealConnectorName(c.name);
      setRevealConnectorId(c.id);
      setShowKeyReveal(true);
      fetchConnectors();
      toast.success('New API key generated');
    } catch {
      toast.error('Failed to regenerate key');
    }
  };

  const handleViewHistory = async (c) => {
    setHistoryConnectorName(c.name);
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const res = await api.get(`/connectors/${c.id}/history`);
      setHistoryData(res.data.history || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'curl') { setCopiedCurl(true); setTimeout(() => setCopiedCurl(false), 2000); }
    else { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const curlExample = `curl -X POST "${API_URL}/api/ingest/${revealConnectorId}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${revealedKey}" \\
  -d '[
    {"supplier_name": "Acme Corp", "spend_amount": 15000, "currency": "USD", "category": "IT Services"},
    {"supplier_name": "Global Ltd", "spend_amount": 8500, "currency": "GBP", "category": "Professional Services"}
  ]'`;

  // Stats
  const totalConnectors = connectors.length;
  const activeConnectors = connectors.filter(c => c.is_active).length;
  const totalIngested = connectors.reduce((s, c) => s + (c.total_records_ingested || 0), 0);

  return (
    <div className="space-y-6" data-testid="connectors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="connectors-title">Data Connectors</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your ERP, accounting, and procurement systems for automated data ingestion
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHelp(true)} data-testid="help-button">
            <HelpCircle className="w-4 h-4 mr-1.5" /> Integration Guide
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-connector-button">
            <Plus className="w-4 h-4 mr-1.5" /> New Connector
          </Button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Connectors', value: totalConnectors, icon: Cable, color: 'text-indigo-500', help: 'Configured data pipelines from ERP, accounting and procurement systems (SAP, Oracle, QuickBooks, Xero, SFTP, API and more).' },
          { label: 'Active', value: activeConnectors, icon: Activity, color: 'text-emerald-500', help: 'Connectors currently enabled and running on their configured sync schedule. Inactive connectors keep their config but do not pull data.' },
          { label: 'Records Ingested', value: totalIngested.toLocaleString(), icon: Database, color: 'text-amber-500', help: 'Cumulative transaction rows pulled into SpendLens through all connectors. Each ingest refreshes analytics and retrains the ML models.' },
        ].map((stat) => (
          <Card key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`p-2.5 rounded-lg bg-muted ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
                  {stat.label}
                  <HelpBubble text={stat.help} />
                </p>
                <p className="text-2xl font-bold font-mono tracking-tight">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Connectors List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading connectors...
        </div>
      ) : connectors.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Cable className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h3 className="text-lg font-semibold mb-1">No Connectors Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first connector to start ingesting data from external systems
            </p>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="empty-create-button">
              <Plus className="w-4 h-4 mr-1.5" /> Create Connector
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence>
            {connectors.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  className="hover:shadow-md transition-shadow duration-200"
                  data-testid={`connector-card-${c.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{SOURCE_ICONS[c.source_type] || '⚙'}</span>
                        <div>
                          <h3 className="font-semibold text-sm">{c.name}</h3>
                          <p className="text-xs text-muted-foreground">{c.source_label}</p>
                        </div>
                      </div>
                      <Badge
                        variant={c.is_active ? 'default' : 'secondary'}
                        className={c.is_active ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : ''}
                        data-testid={`connector-status-${c.id}`}
                      >
                        {c.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {c.description && (
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{c.description}</p>
                    )}

                    <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                      <div>
                        <span className="text-muted-foreground block">API Key</span>
                        <span className="font-mono font-medium">{c.api_key_prefix}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Records</span>
                        <span className="font-mono font-medium">{(c.total_records_ingested || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Last Ingest</span>
                        <span className="font-medium">
                          {c.last_ingestion_at
                            ? new Date(c.last_ingestion_at).toLocaleDateString()
                            : 'Never'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => handleToggle(c)}
                        data-testid={`toggle-connector-${c.id}`}
                      >
                        {c.is_active
                          ? <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</>
                          : <><Power className="w-3.5 h-3.5 mr-1" /> Activate</>}
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => handleViewHistory(c)}
                        data-testid={`history-connector-${c.id}`}
                      >
                        <History className="w-3.5 h-3.5 mr-1" /> History
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => handleRegenKey(c)}
                        data-testid={`regen-key-${c.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> New Key
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setShowDeleteConfirm(c.id)}
                        data-testid={`delete-connector-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* ── Create Connector Dialog ── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-connector-dialog">
          <DialogHeader>
            <DialogTitle>New Connector</DialogTitle>
            <DialogDescription>Connect an external system to ingest spend data automatically</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Connector Name *</Label>
              <Input
                placeholder="e.g. Production SAP"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="connector-name-input"
              />
            </div>
            <div>
              <Label>Source Type *</Label>
              <Select value={formSource} onValueChange={handleSourceChange}>
                <SelectTrigger data-testid="source-type-select">
                  <SelectValue placeholder="Select a source system" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(sourceTypes).map(([key, st]) => (
                    <SelectItem key={key} value={key} data-testid={`source-option-${key}`}>
                      {SOURCE_ICONS[key] || '⚙'} {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Optional notes about this connector..."
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                rows={2}
                data-testid="connector-desc-input"
              />
            </div>
            {formSource && Object.keys(formMapping).length > 0 && (
              <div>
                <Label className="mb-2 block">Field Mapping</Label>
                <p className="text-xs text-muted-foreground mb-2">Maps your source fields to SpendLens standard fields. Edit as needed.</p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-0 text-xs font-semibold uppercase tracking-wider bg-muted/50 p-2 px-3">
                    <span>Source Field</span>
                    <span></span>
                    <span>SpendLens Field</span>
                  </div>
                  {Object.entries(formMapping).map(([src, tgt]) => (
                    <div key={src} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-1.5 border-t border-border text-sm">
                      <span className="font-mono text-xs truncate">{src}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <Input
                        className="h-7 text-xs font-mono"
                        value={tgt}
                        onChange={(e) => setFormMapping(prev => ({ ...prev, [src]: e.target.value }))}
                        data-testid={`mapping-${src}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createLoading} data-testid="create-submit-button">
              {createLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Create Connector
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── API Key Reveal Dialog ── */}
      <Dialog open={showKeyReveal} onOpenChange={(open) => { if (!open) { setRevealedKey(''); } setShowKeyReveal(open); }}>
        <DialogContent className="max-w-lg" data-testid="key-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" /> API Key for "{revealConnectorName}"
            </DialogTitle>
            <DialogDescription>
              <span className="flex items-center gap-1.5 mt-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                Copy this key now. It will not be shown again.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API Key</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-3 bg-muted rounded-md text-xs font-mono break-all select-all" data-testid="api-key-value">
                  {revealedKey}
                </code>
                <Button
                  variant="outline" size="icon"
                  onClick={() => copyToClipboard(revealedKey, 'key')}
                  data-testid="copy-key-button"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">cURL Example</Label>
              <div className="relative mt-1">
                <pre className="p-3 bg-muted rounded-md text-[11px] font-mono overflow-x-auto whitespace-pre-wrap" data-testid="curl-example">
                  {curlExample}
                </pre>
                <Button
                  variant="ghost" size="sm"
                  className="absolute top-1.5 right-1.5"
                  onClick={() => copyToClipboard(curlExample, 'curl')}
                  data-testid="copy-curl-button"
                >
                  {copiedCurl ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setShowKeyReveal(false); setRevealedKey(''); }} data-testid="done-key-button">
              Done — I've saved the key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Ingestion History Dialog ── */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>Ingestion History — {historyConnectorName}</DialogTitle>
            <DialogDescription>Recent data ingestion events</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading...
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No ingestion events recorded yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Time</th>
                    <th className="text-right py-2 px-2">Received</th>
                    <th className="text-right py-2 px-2">Processed</th>
                    <th className="text-right py-2 px-2">Failed</th>
                    <th className="text-left py-2 px-2">Source IP</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((h) => (
                    <tr key={h.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                      <td className="py-2 px-2">
                        {h.status === 'completed' ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                          </Badge>
                        ) : h.status === 'failed' ? (
                          <Badge variant="outline" className="bg-rose-500/10 text-rose-600 border-rose-500/20 text-[10px]">
                            <XCircle className="w-3 h-3 mr-1" /> Failed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">
                            <Clock className="w-3 h-3 mr-1" /> Processing
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {new Date(h.started_at).toLocaleString()}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-xs">{h.records_received}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-emerald-600">{h.records_processed}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-rose-500">{h.records_failed}</td>
                      <td className="py-2 px-2 text-xs font-mono text-muted-foreground">{h.source_ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Connector?</DialogTitle>
            <DialogDescription>
              This will permanently remove the connector, its API key, and all ingestion history. Ingested data will remain.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm)} data-testid="confirm-delete-button">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Help / Integration Guide Dialog ── */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle>Integration Guide</DialogTitle>
            <DialogDescription>How to connect your systems to SpendLens</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2 text-sm">
            <section>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-indigo-500" /> Quick Start
              </h3>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li><strong className="text-foreground">Create a connector</strong> — Choose your source system (SAP, Oracle, QuickBooks, etc.)</li>
                <li><strong className="text-foreground">Save your API key</strong> — Shown once on creation. Store it securely.</li>
                <li><strong className="text-foreground">Send data via API</strong> — POST JSON records to your ingest endpoint.</li>
                <li><strong className="text-foreground">Monitor</strong> — Check ingestion history for status and error details.</li>
              </ol>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <Cable className="w-4 h-4 text-indigo-500" /> API Reference
              </h3>
              <div className="bg-muted rounded-lg p-4 font-mono text-xs space-y-1">
                <p><span className="text-emerald-600 font-semibold">POST</span> {API_URL}/api/ingest/{'<connector_id>'}</p>
                <p className="text-muted-foreground mt-2">Headers:</p>
                <p>  Content-Type: application/json</p>
                <p>  X-API-Key: {'<your_api_key>'}</p>
                <p className="text-muted-foreground mt-2">Body (JSON array):</p>
                <pre className="mt-1 whitespace-pre-wrap">{`[
  {"supplier_name": "Acme", "spend_amount": 5000, "currency": "USD"},
  {"supplier_name": "Beta Co", "spend_amount": 12000, "currency": "EUR"}
]`}</pre>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <Database className="w-4 h-4 text-indigo-500" /> Field Mapping
              </h3>
              <p className="text-muted-foreground mb-2">
                Each connector has a field mapping that translates your source field names into SpendLens standard fields.
                Default mappings are provided per source type, and can be customized.
              </p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="font-semibold bg-muted p-1.5 px-2 rounded-t">Required Fields</div>
                <div className="font-semibold bg-muted p-1.5 px-2 rounded-t">Description</div>
                <div className="p-1.5 px-2 font-mono border-b border-border">supplier_name</div>
                <div className="p-1.5 px-2 border-b border-border">Vendor/supplier name</div>
                <div className="p-1.5 px-2 font-mono border-b border-border">spend_amount</div>
                <div className="p-1.5 px-2 border-b border-border">Transaction amount (numeric)</div>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-indigo-500" /> Tips
              </h3>
              <ul className="space-y-1.5 text-muted-foreground list-disc list-inside">
                <li>You can send up to <strong className="text-foreground">5,000 records</strong> per request.</li>
                <li>Records missing <code className="text-xs bg-muted px-1 rounded">supplier_name</code> or <code className="text-xs bg-muted px-1 rounded">spend_amount</code> will be skipped.</li>
                <li>Regenerate your API key anytime — the old key stops working immediately.</li>
                <li>Deactivate a connector to pause ingestion without deleting it.</li>
                <li>Manual CSV/Excel upload on the Upload page remains available.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                <Activity className="w-4 h-4 text-indigo-500" /> Supported Sources
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(sourceTypes).map(([key, st]) => (
                  <div key={key} className="flex items-center gap-2 p-2 rounded-md bg-muted/50 text-xs">
                    <span>{SOURCE_ICONS[key] || '⚙'}</span>
                    <span className="font-medium">{st.label}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
